import { MONEY_UNIT } from '@web-platform/shared-universal-constants/src/money';
import {
  formatCurrency,
  formatCurrencyShort,
} from '@web-platform/shared-universal-utils-frontend-localisation';

import type { SkyscannerCommonV1Money } from '@skyscanner-internal/sonar-web-http-client';
import type { Money, MoneyUnit } from '@web-platform/shared-universal-constants/src/money';

const moneyUnitDivider: { [key in MoneyUnit]?: number | undefined } = {
  [MONEY_UNIT.UNIT_WHOLE]: 1,
  [MONEY_UNIT.UNIT_CENTI]: 100,
  [MONEY_UNIT.UNIT_MILLI]: 1_000,
  [MONEY_UNIT.UNIT_MICRO]: 1_000_000,
};

export function formatMoney(money: Money | undefined): string;
export function formatMoney(money: Money | undefined, includeCurrency: false): number;
export function formatMoney(money: Money | undefined, includeCurrency: true): string;
export function formatMoney(money: Money | undefined, includeCurrency = true): string | number {
  const divider = money?.unit && moneyUnitDivider[money.unit];
  if (!divider) {
    throw new Error(`Unrecognised money unit: ${money?.unit}`);
  }

  const moneyAmount = Number(money.amount) / divider;
  if (!includeCurrency) {
    return moneyAmount;
  }
  return formatCurrency(moneyAmount);
}

export function formatMoneyShort(
  money: Money | undefined,
  options?: {
    minimumSignificantDigits?: number;
    maximumSignificantDigits?: number;
    compact?: 'short' | 'long';
  },
): string {
  const divider = money?.unit && moneyUnitDivider[money.unit];
  if (!divider) {
    throw new Error(`Unrecognised money unit: ${money?.unit}`);
  }

  if (!money.currency_code) {
    throw new Error(`Currency code is required: ${money}`);
  }

  const moneyAmount = Number(money.amount) / divider;
  return formatCurrencyShort(moneyAmount, money.currency_code, options);
}

// It also accepts RadarMoney type, which is the same as SkyscannerCommonV1Money.Money
// RadarMoney is for unified search which isn't going through the grpc gateway yet - we can remove this local type once it is
export const mapMoney = (money: SkyscannerCommonV1Money.Money | undefined): Money | undefined =>
  money
    ? {
        amount: money.amount,
        currency_code: money.currencyCode,
        unit: money.unit,
      }
    : undefined;
